import { Analytics } from '@cobliteam/analytics';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import { Textarea } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import ShineBorder from '@cobliteam/ui-business/components/border/ShineBorder';
import type { KeyboardEventHandler } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAIAssistant } from '../store';
import AIBetaFlag from './AIBetaFlag';
import AIButtonFloat from './AIButtonFloat';
import AIIcon from './AIIcon';
import { PromptInputSubmit } from './elements/PromptInput';

const AI_ROUTE_PREFIX = '/ai';
const FLOATING_INPUT_MAX_WIDTH = 480;
const FLOATING_INPUT_HORIZONTAL_GUTTER = 32;

interface FloatingInputBounds {
  centerX: number;
  width: number;
}

const AIAssistantFloatingInput = () => {
  const { t } = useTranslation('ai');
  const { isAISidepanelOpen, setPendingInput, openAISidepanel } = useAIAssistant();
  const { pathname } = useLocation();
  const [input, setInput] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);
  const [floatingInputBounds, setFloatingInputBounds] = useState<FloatingInputBounds | null>(null);
  /** Same storage key as the rest of the dashboard (`e2e/playwright/tools/localStorage.ts` seeds `preferences`). */
  const [preferences] = useLocalStorage<Preferences>('preferences');
  const isHiddenAssistantFloatingInput = Boolean(preferences?.hideAIAssistantFloatingInput);

  const handleBeforeToggle = useCallback(() => {
    const trimmed = input.trim();
    if (trimmed) {
      setPendingInput(trimmed);
      setInput('');
    }
  }, [input, setPendingInput]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      const form = e.currentTarget.form;
      if (form) form.requestSubmit();
    }
  };

  const { aiChat } = useFeatureFlags();

  const isHiddenRoute = pathname.startsWith(AI_ROUTE_PREFIX) || pathname.startsWith('/monitoring') || pathname.startsWith('/tracking');

  useEffect(() => {
    const pageElement = document.getElementById('app-body');

    if (!pageElement) return;

    const updateFloatingInputBounds = () => {
      const rect = pageElement.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width - FLOATING_INPUT_HORIZONTAL_GUTTER, 0), FLOATING_INPUT_MAX_WIDTH);

      setFloatingInputBounds({
        centerX: rect.left + rect.width / 2,
        width,
      });
    };

    // Keep the floating input aligned with the page container instead of the full viewport.
    updateFloatingInputBounds();

    const resizeObserver = new ResizeObserver(updateFloatingInputBounds);

    resizeObserver.observe(pageElement);
    window.addEventListener('resize', updateFloatingInputBounds);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateFloatingInputBounds);
    };
  }, []);

  if (!aiChat) return null;
  if (isHiddenAssistantFloatingInput) return null;
  if (isHiddenRoute) return null;
  if (isAISidepanelOpen) return null;
  if (isDismissed) return <AIButtonFloat />;

  const hasContent = Boolean(input.trim());

  return (
    <div
      data-testid='ai-floating-input'
      className='-translate-x-1/2 pointer-events-none fixed bottom-16 left-1/2 z-[60] w-[calc(100vw-2rem)] max-w-[480px]'
      style={
        floatingInputBounds
          ? {
              left: floatingInputBounds.centerX,
              width: floatingInputBounds.width,
            }
          : undefined
      }
    >
      <div className='pointer-events-auto relative w-full max-w-[480px] rounded-lg shadow-md'>
        <ShineBorder borderWidth={2} className='absolute inset-0 rounded-lg' shineColor={['#0745cf', '#9847ee', '#a07cfe']} />
        <div className={cn('relative overflow-hidden rounded-lg p-[1.5px]')}>
          <div className='relative flex flex-col rounded-md bg-white shadow-md'>
            {/* Header */}
            <div className='flex items-center justify-between px-3 pt-2 pb-0'>
              <span className='font-semibold text-gray-11 text-sm'>
                Pergunte a <span className='bg-gradient-to-r from-[#0745cf] to-[#9847ee] bg-clip-text text-transparent'>Cobli IA</span>
                <AIBetaFlag className='ml-1.5' />
              </span>
              <Button
                variant='ghost'
                size='sm'
                icon={<Icon name='x' size='sm' />}
                className='h-8 w-8 p-0 text-gray-9 hover:text-gray-11'
                onClick={() => {
                  Analytics.track('AI - Chat - Dismissed', {
                    source: 'floating',
                  });
                  setIsDismissed(true);
                }}
                aria-label={t('assistant_float.aria_label')}
                data-testid='ai-floating-input-dismiss'
              />
            </div>

            {/* Divider */}
            <div className='px-0 py-0.5'>
              <div className='h-px w-full bg-gray-5' />
            </div>

            {/* Input row */}
            <form
              className='flex items-center px-3 pt-1 pb-2'
              onSubmit={(e) => {
                e.preventDefault();
                const prompt = input.trim();
                Analytics.track('AI - Chat - Message Sent', {
                  prompt,
                  source: 'floating',
                });
                Analytics.track('AI - Chat - Open New Conversation', {
                  source: 'floating',
                });
                handleBeforeToggle();
                openAISidepanel();
              }}
            >
              <AIIcon size={32} isHovered sparklesPaint='brand-gradient' />
              <div className='min-w-0 flex-1'>
                <Textarea
                  placeholder={t('prompt_placeholder')}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className='!border-none !shadow-none !text-sm w-full resize-none rounded-none bg-transparent px-2 py-2.5 leading-5 outline-none placeholder:text-gray-8 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0'
                  style={{ minHeight: 32, maxHeight: 80 }}
                />
              </div>
              <div className='flex shrink-0 items-center gap-1.5'>
                <PromptInputSubmit disabled={!hasContent} status='ready' variant='solid' />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantFloatingInput;
